import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import type { Heat } from '../../../types/heats';
import type { ReseedScope } from '../../../services/heats-reseed.service';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  heats: Heat[];
  futureDates: string[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
  loading: boolean;
  onConfirm: (scope: ReseedScope) => void;
}

/** Une heat courue ou en cours n'est jamais réorganisée (le serveur l'exclut). */
const VERROUILLEES = new Set(['running', 'finished', 'completed', 'preparing', 'countdown']);

/** Clé du public d'une heat : sa catégorie, ou son groupe de catégories. */
const clePublic = (h: Heat) =>
  h.category_group_id ? `group:${h.category_group_id}` : h.category_id ? `cat:${h.category_id}` : '';

/**
 * Réorganisation des heats d'une journée selon le classement général.
 *
 * Le périmètre (BoL 2026-10-03) : on choisit les WODs et les catégories à
 * réorganiser ; les autres heats de la journée ne bougent pas. Tout est coché
 * à l'ouverture, ce qui reproduit l'ancien comportement (toute la journée).
 */
const ReseedHeatsDialog: React.FC<Props> = ({
  open, onOpenChange, heats, futureDates, selectedDate, onSelectDate, loading, onConfirm,
}) => {
  const { t } = useTranslation();
  const longDate = (d: string) =>
    new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' });

  const heatsDuJour = useMemo(
    () => heats.filter(h => h.date === selectedDate && !VERROUILLEES.has((h.status || '').toLowerCase())),
    [heats, selectedDate],
  );

  const wods = useMemo(() => {
    const m = new Map<string, { label: string; num: number }>();
    heatsDuJour.forEach(h => {
      if (!h.wod_id || m.has(h.wod_id)) return;
      const num = h.wod_number ?? 0;
      m.set(h.wod_id, { label: `${num ? `WOD ${num} · ` : ''}${h.wod_name || 'WOD'}`, num });
    });
    return Array.from(m.entries()).sort((a, b) => a[1].num - b[1].num);
  }, [heatsDuJour]);

  const publics = useMemo(() => {
    const m = new Map<string, string>();
    heatsDuJour.forEach(h => {
      const cle = clePublic(h);
      if (!cle || m.has(cle)) return;
      const nom = h.category_group_id && h.category_names?.length
        ? h.category_names.join(' + ')
        : h.category_name || '—';
      m.set(cle, nom);
    });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [heatsDuJour]);

  const [wodsCoches, setWodsCoches] = useState<Set<string>>(new Set());
  const [publicsCoches, setPublicsCoches] = useState<Set<string>>(new Set());

  // À chaque ouverture ou changement de jour : tout coché.
  useEffect(() => {
    if (!open) return;
    setWodsCoches(new Set(wods.map(([id]) => id)));
    setPublicsCoches(new Set(publics.map(([cle]) => cle)));
  }, [open, wods, publics]);

  const basculer = (set: Set<string>, setter: (s: Set<string>) => void, cle: string) => {
    const s = new Set(set);
    if (s.has(cle)) s.delete(cle); else s.add(cle);
    setter(s);
  };

  const heatsVisees = heatsDuJour.filter(
    h => h.wod_id && wodsCoches.has(h.wod_id) && publicsCoches.has(clePublic(h)),
  );

  const confirmer = () => {
    const toutesWods = wodsCoches.size === wods.length;
    const tousPublics = publicsCoches.size === publics.length;
    const cles = Array.from(publicsCoches);
    onConfirm({
      wod_ids: toutesWods ? undefined : Array.from(wodsCoches),
      category_ids: tousPublics ? undefined
        : cles.filter(c => c.startsWith('cat:')).map(c => c.slice(4)),
      category_group_ids: tousPublics ? undefined
        : cles.filter(c => c.startsWith('group:')).map(c => c.slice(6)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('heats.reseedDialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('heats.reseedDialog.dateToReorganize')}</Label>
            <Select value={selectedDate} onValueChange={onSelectDate}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {futureDates.map(date => (
                  <SelectItem key={date} value={date}>{longDate(date)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {wods.length > 0 && (
            <div className="space-y-2">
              <Label>{t('heats.reseedDialog.wods')}</Label>
              <div className="space-y-1.5">
                {wods.map(([id, { label }]) => (
                  <label key={id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={wodsCoches.has(id)}
                      onCheckedChange={() => basculer(wodsCoches, setWodsCoches, id)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {publics.length > 0 && (
            <div className="space-y-2">
              <Label>{t('heats.reseedDialog.categories')}</Label>
              <div className="space-y-1.5">
                {publics.map(([cle, nom]) => (
                  <label key={cle} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={publicsCoches.has(cle)}
                      onCheckedChange={() => basculer(publicsCoches, setPublicsCoches, cle)}
                    />
                    {nom}
                  </label>
                ))}
              </div>
            </div>
          )}

          <Alert variant="warning">
            <AlertDescription>
              {t('heats.reseedDialog.warningMessage', {
                date: selectedDate && longDate(selectedDate),
              })}
            </AlertDescription>
          </Alert>

          <p className="text-sm text-muted-foreground">
            {t('heats.reseedDialog.heatsAffected', { count: heatsVisees.length })}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={confirmer}
            disabled={!selectedDate || loading || heatsVisees.length === 0}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('heats.reseedDialog.confirmReorganize')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReseedHeatsDialog;
